import { useEffect, useRef } from 'react';
import type { UnlockId } from '@/core/content/ids';
import { unlockStatus } from '@/core/domain/unlocks';
import { useBack } from '@/ui/back/backStack';
import { useI18n } from '@/ui/i18n/I18nProvider';
import { useSave, useServices } from '@/ui/services/ServicesContext';
import styles from './UpcomingPopover.module.css';

/** At most this many upcoming unlocks (ux-screens §2.3: "liệt kê ≤ 3 unlock kế tiếp"). */
const MAX_ROWS = 3;

/**
 * "Sắp mở" (ux-screens §2.3): tapping the Stars chip lists the next unlocks and what each still needs.
 * Paid areas that are ready come first with their "Mở rộng" button; hidden teasers stay hidden.
 */
export function UpcomingPopover({
  onClose,
  onExpand,
  onSeeAll,
}: {
  onClose: () => void;
  onExpand: (unlockId: UnlockId) => void;
  /** "Xem tất cả": the full list of unlocks and what each still needs. */
  onSeeAll?: () => void;
}) {
  const { t } = useI18n();
  const { content } = useServices();
  const ref = useRef<HTMLDivElement>(null);
  useBack(onClose);
  // A string key keeps the selector stable (no new arrays per store update).
  const key = useSave((s) =>
    [...content.unlocks.values()]
      .filter((d) => !s.unlockedIds.includes(d.id))
      .map((d) => {
        const st = unlockStatus(content, s, d.id);
        return `${d.id}:${st.kind}`;
      })
      .join(','),
  );
  const stars = useSave((s) => s.stars.total);

  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    window.addEventListener('pointerdown', onDown, true);
    return () => {
      window.removeEventListener('pointerdown', onDown, true);
    };
  }, [onClose]);

  const rows = key
    .split(',')
    .filter(Boolean)
    .flatMap((part) => {
      const [id, kind] = part.split(':') as [UnlockId, string];
      const def = content.unlocks.get(id);
      return def && (kind === 'purchasable' || def.previewWhenLocked) ? [{ def, kind }] : [];
    })
    .sort(
      (a, b) =>
        (a.kind === 'purchasable' ? 0 : 1) - (b.kind === 'purchasable' ? 0 : 1) || a.def.order - b.def.order,
    )
    .slice(0, MAX_ROWS);

  return (
    <div
      ref={ref}
      className={styles.popover}
      role="dialog"
      aria-label={t('ui.upcoming.title')}
      data-testid="upcoming"
    >
      <p className={styles.title}>{t('ui.upcoming.title')}</p>
      {rows.length === 0 ? (
        <p className={styles.empty}>{t('ui.upcoming.all_done')}</p>
      ) : (
        <ul className={styles.list}>
          {rows.map(({ def, kind }) => {
            const need = def.prerequisites.find((c) => c.type === 'starsAtLeast');
            return (
              <li key={def.id} className={styles.row}>
                <span className={styles.name}>{t(def.nameKey)}</span>
                {kind === 'purchasable' ? (
                  <button
                    type="button"
                    className={styles.expand}
                    onClick={() => {
                      onExpand(def.id);
                    }}
                  >
                    {t('ui.upcoming.expand')}
                  </button>
                ) : (
                  <span className={styles.reason}>
                    {t(def.reasonKey, { need: need?.type === 'starsAtLeast' ? need.value : 0, have: stars })}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {onSeeAll && (
        <button type="button" className={styles.seeAll} data-testid="upcoming-all" onClick={onSeeAll}>
          {t('ui.upcoming.see_all')}
        </button>
      )}
    </div>
  );
}
